/* eslint-disable */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useParams
} from "react-router-dom";
import { shallow } from "zustand/shallow";
import {
  GridMain,
  Modal,
  Spinner,
  Tabs,
  Video
} from "../../components";
import {
  LEVEL,
  TAB_ITEMS_CONFIG
} from "../../consts";
import {
  useLearningObjectQuery,
  useRelatedLearningObjectQuery,
  useStartOrResumeQuery,
  useToolsQuery
} from "../../queries";
import { useSharedPosts } from "../../queries/community";
import { useNavigate } from "../../services";
import {useAuthStore, useCommunityClusterStore, useLanguageStore, useNavbarStore} from "../../stores";
import type {
  Course,
  Level,
  Root
} from "../../types";
import { getError } from "../../utils/error";
import { getPreviousRoute } from "../../utils/general";
import {
  filterTabTypes,
  getExploreTabProps
} from "../../utils/explore";

const EXTERNAL_LEVEL = {
  [LEVEL.DA_SCORM]: LEVEL.DA_SCORM,
  [LEVEL.DA_XAPI]: LEVEL.DA_XAPI,
  [LEVEL.DA_SURVEY]: LEVEL.DA_SURVEY,
  [LEVEL.DA_YOUTUBE]: LEVEL.DA_YOUTUBE
} as const;

export function ExploreWatch() {
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);
  const { t } = useTranslation();



  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [showModal, setShowModal] = useState(false);
  const navbarItem = useNavbarStore(state => state.item);

  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const enableCommunityFlag = useAuthStore(state => state.corporateInfo?.enable_community_flag);

  const { id: idString, learningObjectTypology, courseId: courseIdString, rootId: rootIdString } = useParams<{
    id: string
    learningObjectTypology: Level,
    courseId?: string,
    rootId?: string,
  }>();

  // Validazione più robusta dei parametri
  const idNumber = idString && !isNaN(Number(idString)) ? Number(idString) : NaN;
  let courseIdNumber = courseIdString && !isNaN(Number(courseIdString)) ? Number(courseIdString) : NaN;
  let rootIdNumber = rootIdString && !isNaN(Number(rootIdString)) ? Number(rootIdString) : NaN;

  // Early return se id non è valido
  if (isNaN(idNumber) || !learningObjectTypology) {
    return <Navigate to="/not-found" replace />;
  }
  if (learningObjectTypology === LEVEL.PATH){
    rootIdNumber = idNumber;
  }
  if (learningObjectTypology === LEVEL.COURSE){
    courseIdNumber = idNumber;
  }

  let parentId: string;
  let grandParentId: string;
  if (rootIdString && courseIdString){
    parentId = courseIdString;
    grandParentId = rootIdString;
  } else {
    parentId = rootIdString || courseIdString || "";
    grandParentId = "";
  }

  const language = useLanguageStore(state => state.language)?.id;

  const {
    data: learningObject,
    error:learningObjectError,
    fetchStatus,
    isLoading: learningObjectIsLoading,
    isError:learningObjectIsError
  } = useStartOrResumeQuery({
    enabled: !isNaN(idNumber) && Boolean(learningObjectTypology),
    language,
    grandParentId: grandParentId,
    learningObjectId: idNumber,
    learningObjectTypology: (learningObjectTypology as Level),
    parentId: parentId
  });

  useEffect(()=> {
    if(learningObjectError){
      setShowModal(true);
    }
  }, [learningObjectError]);

  const courseDetailsId = learningObject &&  "courseDetails" in learningObject && learningObject?.courseDetails?.id ?
    learningObject?.courseDetails?.id : courseIdNumber ? courseIdNumber : null;

  const rootDetailsId = learningObject &&  "rootDetails" in learningObject && learningObject?.rootDetails?.id ?
    learningObject?.rootDetails?.id : rootIdString ? rootIdString : null;

  // const isExternal = learningObjectTypology === EXTERNAL_LEVEL.DA_SCORM
  //   || learningObjectTypology === EXTERNAL_LEVEL.DA_XAPI
  //   || learningObjectTypology === EXTERNAL_LEVEL.DA_SURVEY
  //   || learningObjectTypology === EXTERNAL_LEVEL.DA_YOUTUBE;

  const {
    data: parentCourse,
    refetch: refetchParentCourse
  } = useLearningObjectQuery({
    enabled: Boolean(courseDetailsId) && !isNaN(Number(courseDetailsId)),
    id: courseDetailsId as any ?? "",
    learningObjectTypology: LEVEL.COURSE,
    queryKey: [`parent-course-${idNumber}`],
    rootId: rootDetailsId as any ?? ""
  });

  const {
    data: parentRoot,
    refetch:refetchParentRoot
  } = useLearningObjectQuery({
    enabled: Boolean(rootDetailsId) && !isNaN(Number(rootDetailsId)),
    id: rootDetailsId as any ?? "",
    learningObjectTypology: LEVEL.PATH,
    onlyLearningActivity: true,
    queryKey: [`parent-root-${idString}`]
  });

  const { data: related } = useRelatedLearningObjectQuery({
    enabled: Boolean(idString) && !isNaN(Number(idString)),
    learningObjectId: idString ?? ""
  });

  const availableClusters = useCommunityClusterStore((store) => store.availableClusters, shallow);
  const communityFlag = enableCommunityFlag === true && smartConfiguration?.funcCommunity === true && !!(availableClusters.find(({ is_share_enabled }) => is_share_enabled));

  const { data: sharedPosts } = useSharedPosts({
    enabled: communityFlag && !isNaN(idNumber),
    loId: idNumber
  });

  const { data: tools } = useToolsQuery({
    enabled: Boolean(learningObject?.id),
    learningObjectId: learningObject?.id?.toString() ?? ""
  });


  const activities = (parentCourse && "learningActivities" in parentCourse)
    ? parentCourse.learningActivities
    : [];

  const filteredTabTypes =
  learningObject?.learningObjectTypology ? (
    filterTabTypes({
      activities,
      learningObject,
      related,
      sharedPosts,
      tabItems: TAB_ITEMS_CONFIG[learningObject?.learningObjectTypology].tabItems,
      tools,
      hasCommunityEnabled: communityFlag
    })
  ) : [];

  const contentTabs = filteredTabTypes?.flatMap((tabType) => (
    learningObject ? (
      getExploreTabProps({
        activities,
        courseId: parentCourse?.id,
        isDetailPage: false,
        learningObject,
        parentCourse,
        parentRoot,
        related,
        rootId: rootIdNumber,
        sharedPosts,
        t,
        tabType,
        tools,
        enrollableEditionsWithSessions: [],
        isPollingEditionChange: false,
      })) : []
  ));

  const typology = learningObject?.learningObjectTypology;
  const isExternal = typology === EXTERNAL_LEVEL.DA_SCORM
    || typology === EXTERNAL_LEVEL.DA_XAPI
    || typology === EXTERNAL_LEVEL.DA_SURVEY
    || typology === EXTERNAL_LEVEL.DA_YOUTUBE;


  // MayBe is Necessary if returned sync LO
  // if (
  //   learningObject?.learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
  //   || learningObject?.learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
  //   || learningObject?.learningObjectTypology === LEVEL.DA_LINKEDIN
  // ){
  //   return (
  //     <Navigate
  //       to={ `/esplora/dettaglio/${learningObject.id}/${learningObject.learningObjectTypology}` }
  //       state={ {
  //         courseId: courseDetailsId,
  //         goBackPath: `/esplora/dettaglio/${id}/${learningObjectTypology}`,
  //         rootId: rootDetailsId
  //       } }
  //     />
  //   );
  // }
/*
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {

    const handleVisibilityChange = () => {


      if (!document.hidden){
        refetchStartOresume();
        if(rootDetailsId){
          refetchParentRoot();
        }
        if(courseDetailsId){
          refetchParentCourse();
        }
      }


    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
*/

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data == "closeModal" && learningObject?.isTest) {
        window.location.reload();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [learningObject?.isTest]);

  return (
    <>
      {
        (learningObjectIsLoading && fetchStatus !== "idle" ) ? (
          <Spinner/>
        ) : (
          !learningObject ? null :
            (
              learningObject?.learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
            || learningObject?.learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
            || learningObject?.learningObjectTypology === LEVEL.DA_URL_RES
            || learningObject?.learningObjectTypology === LEVEL.DA_LINKEDIN
            || learningObject?.learningObjectTypology === LEVEL.DA_LTI
            || learningObject?.learningObjectTypology === LEVEL.DA_FINAL_BALANCE
            || learningObject?.learningObjectTypology === LEVEL.DA_MEETING
            || learningObject?.learningObjectTypology === LEVEL.DA_GOALS
            || learningObject?.learningObjectTypology === LEVEL.DA_MATERIAL
            ) ?
              <Navigate
                to={ navigate.getDetailLink({
                  id: learningObject.id,
                  learningObjectTypology: learningObject.learningObjectTypology,
                  courseId: courseDetailsId as any,
                  rootId: rootDetailsId as any
                }) }
              />
              :
              (
                <>
                  <Video
                    key={learningObject.id}
                    autoplay={ true }
                    courseDetails={ parentCourse ? parentCourse as Course : learningObject?.courseDetails }
                    courseId={ courseDetailsId as any || null }
                    coverFallBack={ learningObject.coverPublicURL }
                    isExternal={isExternal}
                    height="750px"
                    id={ learningObject?.id }
                    isMandatory={ learningObject.isMandatory }
                    isFavourite={ learningObject?.isFavourite }
                    isStandAlone={ !rootIdString && !courseIdString }
                    learningObjectType={ learningObject?.learningObjectType }
                    learningObjectTypology={ learningObject?.learningObjectTypology  }
                    learningObjectStarted={ {
                      ...learningObject,
                      id: idNumber === learningObject?.id
                        ? idNumber
                        : learningObject?.id,
                      learningObjectTypology: learningObjectTypology === learningObject?.learningObjectTypology
                        ? learningObjectTypology as Level
                        : learningObject?.learningObjectTypology as Level,
                      grandParentId: grandParentId ? Number(grandParentId) : null,
                      parentId: parentId ? Number(parentId) : null
                    } }
                    moduleNumber={  courseDetailsId || rootDetailsId
                      ? learningObject?.prereqOrderNumber + 1 : undefined  }
                    pathId={ rootDetailsId as any }
                    rating={ learningObject?.averageRatingScore ?? 0 }
                    refetchParentCourse={ refetchParentCourse }
                    refetchParentRoot={ refetchParentRoot }
                    rootDetails={ parentRoot as Root || parentCourse as Course }
                    shortDescription={ learningObject?.shortDescription }
                    status={ learningObject?.status }
                    title={ learningObject?.title }
                    videoId={ learningObject?.brightCoverId?.toString()  }
                    tentativeId={ learningObject?.tentativeId }
                    canAccessNow={ learningObject?.canAccessNow }
                    canAccessUntil={ learningObject?.canAccessUntil }
                    contentTabs={contentTabs}
                  />
                  <GridMain
                    padding={ "0 0 80px" }
                  >
                    <Tabs
                      titleInset={ true }
                      contents={ contentTabs }
                    />
                  </GridMain>
                </>
              )
        )
      }
      <Modal
        description={ getError(learningObjectError)?.[0]?.cause === "LEARNING_OBJECT_OUT_OF_TIME_WINDOW" ? t("learning.object.out.of.time.window") : getError(learningObjectError)?.[0]?.message ?? "" }
        open={ learningObjectIsError && showModal }
        title={
          getError(learningObjectError)?.[0]?.name === "FATAL"
            ? t("attention")
            : getError(learningObjectError)?.[0]?.name ?? ""
        }
        onClose={ ()=> {
          setShowModal(false);
          const previousRoute = getPreviousRoute({
            id: idNumber,
            rootId: rootIdNumber,
            courseId: courseIdNumber,
            path: pathname,
            section: navbarItem,
            learningObjectTypology: learningObjectTypology as Level
          });
          navigate(previousRoute);
        } }
      />
    </>
  );
}
